<template>
  <div class="article-footer">
    <div
      v-if="isShowTags"
      class="article-footer__tags"
      :class="articleTagsClass"
    >
      <ul class="article-footer__tags-list">
        <li class="article-footer__tag-item article-footer__tags-title">Теги</li>
        <li
          v-for="tag of filteredTags"
          :key="tag.id"
          class="article-footer__tag-item"
        >
          <BHLink
            class="article-footer__tag"
            :href="tag.url"
            @click="sendAnalytics(tag)"
            >{{ tag.title }}</BHLink
          >
        </li>
      </ul>
    </div>
    <div
      v-if="isShowInfo"
      class="article-footer__info"
      :class="articleInfoClass"
    >
      <ArticleFooterSourceItem
        v-for="(item, index) of sources"
        :key="index"
        :source="item"
        class="article-footer__info-elem"
        :class="`article-footer__info-elem--${item.type}`"
      />
    </div>
    <div
      v-show="isBigTabletAndMore"
      class="article-footer__info-elem article-footer__error-elem"
      :class="errorElemClass"
    >
      Если нашли ошибку, выделите текст и нажмите Ctrl + Enter
    </div>
    <div
      v-if="!hideSharings"
      class="article-footer__action-list"
    >
      <CardFooter
        class="article-footer__actions"
        :hide-labels="false"
        :hide-share="false"
        :hide-date="true"
        :hide-reactions="false"
        :article="article"
        :hide-categories="true"
        :is-show-comments-as-button="true"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import CardFooter from '~/components/CardFooter.vue';
  import ArticleFooterSourceItem from '~/components/ArticleFooterSourceItem.vue';
  import type { TTag } from '~/types/common';
  import BHLink from '~/components/BHLink.vue';
  import { ADS_LABELS } from '~/constants';
  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import { useSingleArticleStore } from '~/store/singleArticle';
  import { useWindowStore } from '~/store/window';
  import getFullUrl from '~/utils/getFullUrl';

  export interface ISource {
    label: string | undefined;
    type: string;
    url: string;
  }

  export default defineNuxtComponent({
    name: 'ArticleFooter',

    components: {
      BHLink,
      CardFooter,
      ArticleFooterSourceItem,
    },

    provide() {
      return {
        eventItemSharingList: () => 'внизу статьи',
        eventItemComments: () => 'нижняя иконка комментариев',
      };
    },

    props: {
      hideTags: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      hideSharings: {
        type: Boolean as PropType<boolean>,
        default: false,
      },
    },

    setup() {
      const articleStore = useSingleArticleStore();
      const windowStore = useWindowStore();

      return {
        article: articleStore.article,
        isTabletAndMore: computed(() => windowStore.isTabletAndMore),
        isBigTabletAndMore: computed(() => windowStore.isBigTabletAndMore),
      };
    },

    computed: {
      filteredTags(): TTag[] {
        const excludesTagsIds = [
          // prod
          20874, 20873,
          // stage
          11214, 20837,
        ];
        const excludesTagsSlugs = ['noad', 'noadsense', 'kb'];

        return this.article?.tags
          .filter((tag: TTag) => {
            return !excludesTagsIds.includes(tag.id);
          })
          .filter((tag: TTag) => {
            return !excludesTagsSlugs.includes(tag.slug);
          });
      },

      articleMeta(): Record<string, any> {
        return this.article.meta;
      },

      articleInfoClass(): Record<string, boolean> {
        const className = 'article-footer__info';
        return {
          [`${className}--without-tags`]: !this.isShowTags,
          [`${className}--promo`]: this.isPromo,
        };
      },

      articleTagsClass(): Record<string, boolean> {
        const className = 'article-footer__tags';
        return {
          [`${className}--without-info`]: !this.isShowInfo,
        };
      },

      sources(): ISource[] {
        const sources: ISource[] = [];
        const { createSource, articleMeta } = this;

        if (this.isShowAdsSource) {
          sources.push(
            createSource('ads-source', this.createAdsSourceLabel(), articleMeta.ads_source_url),
          );
        }

        if (this.isShowCoverSource) {
          const label = this.getSourceLabel('Обложка:&nbsp;', 'cover_source', 'cover_source_url');
          sources.push(createSource('cover-source', label, articleMeta.cover_source_url));
        }

        if (this.isShowPostSource) {
          const label = this.getSourceLabel('Источник:&nbsp;', 'post_source', 'post_source_url');
          sources.push(createSource('post-source', label, articleMeta.post_source_url));
        }

        if (this.isShowAuthors) {
          const label = this.getSourceLabel('Над текстом работали:&nbsp;', 'authors', '');
          sources.push(createSource('authors', label));
        }
        return sources;
      },

      isShowTags(): boolean {
        return !this.hideTags && this.filteredTags.length > 0;
      },

      isShowInfo(): boolean {
        const meta = this.articleMeta;
        return meta.cover_source || meta.post_source || meta.authors || this.isShowAdsSource;
      },

      isShowAuthors(): boolean {
        return this.articleMeta.authors;
      },

      isShowPostSource(): boolean {
        const meta = this.articleMeta;
        return meta.post_source || meta.post_source_url;
      },

      isShowCoverSource(): boolean {
        const meta = this.articleMeta;
        return meta.cover_source || meta.cover_source_url;
      },

      isPromo(): boolean {
        return this.article?.is_promo;
      },

      isAdvertising(): boolean {
        return this.article.is_advertising;
      },

      isShowAdsSource(): boolean {
        return this.isAdvertising || this.isPromo;
      },

      errorElemClass(): Record<string, any> {
        const className = 'article-footer__error-elem';
        return { [`${className}--promo`]: this.isPromo };
      },

      currentslug(): string {
        return getSlugFromUrl(this.$route.fullPath);
      },
    },

    methods: {
      createSource(type: string, label: string | undefined, url: string = ''): ISource {
        return {
          type,
          label,
          url,
        };
      },

      createAdsSourceLabel(): string {
        const advert_label = this.isPromo ? 'ads' : this.article.advert_label;
        const label = ADS_LABELS[advert_label] || '';
        const text = this.articleMeta.ads_source.trim() || this.articleMeta.ads_source_url.trim();
        return text ? `${label}:&nbsp;${text}` : label;
      },

      getSourceLabel(
        label: string,
        labelPropName: string,
        sourcePropName: string,
      ): string | undefined {
        const labelProp = this.articleMeta[labelPropName];
        const sourceProp = this.articleMeta[sourcePropName];
        return `${label}${labelProp || sourceProp}`;
      },

      sendAnalytics(tag: TTag) {
        this.$sendYandexMetrika({
          level1: 'Клик_теги',
          level4: getFullUrl(tag.url),
          level5: tag.title,
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  .article-footer {
    font-family: $text-font--1;

    &__tags {
      max-height: 48px;
      overflow: hidden;
      margin-bottom: $spacing-6;
      border-bottom: 1px solid $stroke-color--1;
      padding-bottom: $spacing-3;

      &--without-info {
        margin-bottom: $spacing-4;
      }
    }

    &__tag-item {
      display: inline-block;

      @include normalText;

      &:not(.article-footer__tags-title) {
        &::before {
          content: '•';
          color: #c722ca;
          margin-right: $spacing-1;
          @include normalText;
        }
      }

      @include hover {
        &::before {
          color: #83218d;
        }
      }

      &:not(:last-child) {
        margin-right: $spacing-1;
      }

      a {
        @include link-styles;
      }
    }

    &__info {
      &--without-tags {
        padding: $spacing-6 0 $spacing-4;
        border-top: 1px solid $stroke-color--1;
      }

      padding-bottom: $spacing-4;
      border-bottom: 1px solid $stroke-color--1;

      margin-bottom: $spacing-4;

      &--promo {
        display: flex;
        align-items: center;
        flex-direction: column;

        border-top: none;
        border-bottom: none;
        padding-top: 0;
        padding-bottom: 0;

        @include tablet {
          margin-bottom: $spacing-3;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    &__info-elem {
      &:not(:last-child) {
        margin-bottom: $spacing-2;
      }

      color: $text-color--1;
      @include tinyText;
    }

    &__error-elem {
      margin-bottom: $spacing-4 !important;

      &--promo {
        text-align: center;
        color: #5c5c5c;
        margin-bottom: $spacing-6 !important;
      }
    }

    &__action-list {
      padding: 4px 0;
    }
  }

  :deep() {
    .card-footer {
      &__button {
        @include hover {
          color: #000;

          .card-footer {
            &__icon svg path {
              fill: #000;
            }
          }
        }
      }

      .card-footer__categories--category {
        @include hover {
          color: #000;
        }
      }
    }
  }
</style>
